/**
 * MapboxScene - Main 3D map visualization component
 *
 * Integrates Mapbox GL JS with Three.js for 3D building rendering
 * Supports optional aircraft interaction (hover, selection, drag, rotate)
 */

import { useEffect, useEffectEvent, useRef, useState, useMemo } from 'react'
import * as THREE from 'three'
import mapboxgl from 'mapbox-gl'
import { useMapStore, getMapStyleUrl, isStandardStyle, type MapboxConfig } from '@/stores/mapStore'
import { hideClassicStyleLabels } from '@/lib/utils/map-labels'
import { useRampViewConfigStore } from '@/stores/rampViewConfigStore'
import { MapStyleSelector } from '@/components/map/MapStyleSelector'
import { MapboxCustomLayerService, type Building3DConfig, type SceneObjects, type PlacedAircraft3D } from '@/services/3d'
import { useMapBuildings, useMapAllocations } from '@/hooks'
import { useObstacleRendering } from '@/hooks/useObstacleRendering'
import { useSubunitRendering } from '@/hooks/useSubunitRendering'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useCollisionStore } from '@/stores/collisionStore'
import { IdleScheduler } from '@/services/scheduling/IdleScheduler'
import { ENABLE_CAMERA_DEBUG } from '@/constants/debug'
import { useCinemaStore } from '@/stores/cinemaStore'
import 'mapbox-gl/dist/mapbox-gl.css'

// Mapbox access token from environment
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

/**
 * Apply Mapbox configuration properties to the map
 * Standard style uses config properties, classic styles use layer visibility
 */
function applyMapboxConfig(map: mapboxgl.Map, config: MapboxConfig, styleId: string) {
  // Classic (non-Standard) styles: hide labels via layer visibility
  if (!isStandardStyle(styleId as any)) {
    hideClassicStyleLabels(map, config)
    return
  }

  console.log('MapboxScene: Applying Mapbox config for Standard style')

  // Light preset
  map.setConfigProperty('basemap', 'lightPreset', config.lightPreset)

  // Theme (if not default)
  if (config.theme !== 'default') {
    map.setConfigProperty('basemap', 'theme', config.theme)
  }

  // Label visibility
  map.setConfigProperty('basemap', 'showPlaceLabels', config.labels.places)
  map.setConfigProperty('basemap', 'showRoadLabels', config.labels.roads)
  map.setConfigProperty('basemap', 'showPointOfInterestLabels', config.labels.poi)
  map.setConfigProperty('basemap', 'showTransitLabels', config.labels.transit)

  // 3D Objects - use master toggle or individual toggles
  if (!config.objects3d.enabled) {
    // Disable all 3D objects with master toggle
    map.setConfigProperty('basemap', 'show3dObjects', false)
  } else {
    // Enable master, control individual elements
    map.setConfigProperty('basemap', 'show3dObjects', true)
    map.setConfigProperty('basemap', 'show3dBuildings', config.objects3d.buildings)
    map.setConfigProperty('basemap', 'show3dTrees', config.objects3d.trees)
    map.setConfigProperty('basemap', 'show3dLandmarks', config.objects3d.landmarks)
  }

  // Feature visibility
  map.setConfigProperty('basemap', 'showPedestrianRoads', config.features.pedestrianRoads)

  console.log('MapboxScene: Config applied', {
    lightPreset: config.lightPreset,
    poi: config.labels.poi,
    transit: config.labels.transit,
    objects3d: config.objects3d.enabled,
  })
}

/**
 * Setup 3D terrain on the map
 */
function setupTerrain(map: mapboxgl.Map, enabled: boolean, exaggeration: number) {
  const terrainSourceId = 'mapbox-dem'

  if (!enabled) {
    // Remove terrain if disabled
    if (map.getTerrain()) {
      map.setTerrain(null)
    }
    if (map.getSource(terrainSourceId)) {
      map.removeSource(terrainSourceId)
    }
    console.log('MapboxScene: 3D terrain disabled')
    return
  }

  // Add terrain DEM source if not present
  if (!map.getSource(terrainSourceId)) {
    map.addSource(terrainSourceId, {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    })
  }

  // Enable terrain
  map.setTerrain({
    source: terrainSourceId,
    exaggeration: exaggeration,
  })

  console.log('MapboxScene: 3D terrain enabled with exaggeration', exaggeration)
}

/** Refs exposed to parent for interaction */
export interface MapboxSceneRefs {
  map: mapboxgl.Map | null
  sceneObjects: SceneObjects | null
  layerService: MapboxCustomLayerService | null
}

interface MapboxSceneProps {
  className?: string
  locationId?: string            // Load buildings from database for this location
  buildings?: Building3DConfig[] // Or provide buildings directly (overrides locationId)
  /** Pre-merged view settings (location + default preset). When provided,
   *  the map initializes directly at these coordinates instead of the
   *  location's base camera (avoids a visible flyTo on load). */
  initialViewSettings?: import('@/hooks/useMapBuildings').ViewSettings
  /** Disable Three.js shadow maps (default: true) */
  enableShadows?: boolean
  /** Enable aircraft interaction (hover, selection, drag, rotate) */
  interactionEnabled?: boolean
  /**
   * Stream this location's real allocations into the scene (default: true).
   *
   * Set false for surfaces that own their own aircraft imperatively and want
   * NO real allocations at all (the cinematic studio). Keep `locationId` set
   * regardless — without it the scene has no location centre and the 3D
   * transform matrix is built at the wrong origin.
   *
   * Surfaces that want BOTH (real allocations plus their own actors — the event
   * planner) don't need this flag: objects added via `addSceneObject`, or via
   * `addAircraft(data, { externalOwner: true })`, are marked
   * `userData.externalOwner` and the allocation diff below skips them.
   */
  renderAllocations?: boolean
  /** Callback when map and 3D scene are ready */
  onReady?: (refs: MapboxSceneRefs, aircraftData: PlacedAircraft3D[]) => void
  /** Render custom overlay (receives map ref for position calculations) */
  renderOverlay?: (map: mapboxgl.Map | null, aircraftData: PlacedAircraft3D[], loaded3DIds: Set<string>) => React.ReactNode
}

export function MapboxScene({
  className = '',
  locationId,
  buildings: propBuildings,
  initialViewSettings,
  enableShadows = true,
  interactionEnabled = false,
  renderAllocations = true,
  onReady,
  renderOverlay,
}: MapboxSceneProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const layerServiceRef = useRef<MapboxCustomLayerService | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [is3DLayerAdded, setIs3DLayerAdded] = useState(false)
  const [buildingSyncTrigger, setBuildingSyncTrigger] = useState(0)
  // Track which locationId the current 3D layer was built for
  const activeLocationIdRef = useRef<string | undefined>(undefined)
  // Tracks the last camera position set by map init or jumpTo — prevents redundant flyTo
  const settledViewSettingsRef = useRef<string | null>(null)
  // Whether the initial camera position has been applied (first = instant, subsequent = animated)
  const hasInitialPositionRef = useRef(false)
  // Tracks the active Mapbox style — prevents redundant setStyle on mount
  const activeStyleRef = useRef<string | null>(null)
  // Track which allocation IDs have loaded 3D models (for sprite→3D fade)
  const [loaded3DIds, setLoaded3DIds] = useState<Set<string>>(new Set())
  // Highlight state tracking — targeted updates instead of clear-all/re-apply
  const prevHoveredRef = useRef<string | null>(null)
  const prevSelectedRef = useRef<string | null>(null)
  const originalEmissivesRef = useRef(new Map<string, THREE.Color>())

  // Aircraft editor store for highlighting
  const hoveredAllocationId = useAircraftEditorStore((state) => state.hoveredAllocationId)
  const selectedAllocationId = useAircraftEditorStore((state) => state.selectedAllocationId)
  const setMapCenter = useAircraftEditorStore((state) => state.setMapCenter)

  // Collision store for collision-aware highlighting
  const collidingAircraftIds = useCollisionStore((state) => state.collidingAircraftIds)

  // Cinema mode hides the scene's own chrome (overlay + map style selector)
  const cinemaActive = useCinemaStore((state) => state.active)

  // Fetch buildings from database if locationId is provided
  const {
    buildings: dbBuildings,
    center: locationCenter,
    viewSettings: dbViewSettings,
  } = useMapBuildings(locationId)

  // Fetch aircraft allocations for 3D rendering.
  // Passing undefined disables the query (it is already `enabled: !!locationId`)
  // so a scene that owns its aircraft imperatively doesn't fetch them at all.
  const {
    aircraft: dbAircraft,
  } = useMapAllocations(renderAllocations ? locationId : undefined)

  // Get state from store
  const {
    viewSettings: storeViewSettings,
    mapStyleId,
    setLoading,
    config,
  } = useMapStore()

  // Mapbox configuration
  const mapboxConfig = config.mapbox

  // View settings priority: initialViewSettings (pre-merged with default preset) > dbViewSettings > storeViewSettings
  // Returns undefined while location data is loading — viewSettingsReady guards prevent premature use
  const viewSettings = useMemo(() => {
    if (locationId) return initialViewSettings ?? dbViewSettings // undefined while loading
    return storeViewSettings // Only used when no locationId (standalone/demo mode)
  }, [locationId, initialViewSettings, dbViewSettings, storeViewSettings])

  // Whether view settings are ready — always wait for initialViewSettings when locationId is known
  // (RampPage always passes it). This prevents init with dbViewSettings then transitioning to preset.
  const viewSettingsReady = !locationId || !!initialViewSettings

  // Priority: propBuildings > dbBuildings > empty (no fallback test data)
  const buildingsToRender = useMemo(() => {
    if (propBuildings && propBuildings.length > 0) {
      return propBuildings
    }
    if (dbBuildings && dbBuildings.length > 0) {
      return dbBuildings
    }
    return []
  }, [propBuildings, dbBuildings])

  // Wire obstacle rendering — fetches obstacles by location, pushes to 3D layer
  // is3DLayerAdded ensures re-render when layerServiceRef gets set
  useObstacleRendering({
    locationId,
    buildings: buildingsToRender,
    layerService: is3DLayerAdded ? layerServiceRef.current : null,
    collisionEnabled: true,
    syncTrigger: buildingSyncTrigger,
  })

  // Wire subunit rendering — fetches subunits, pushes to 3D layer
  useSubunitRendering({
    locationId,
    buildings: buildingsToRender,
    layerService: is3DLayerAdded ? layerServiceRef.current : null,
    syncTrigger: buildingSyncTrigger,
  })

  // useEffectEvent: read latest viewSettings without adding to effect deps (React 19.2)
  const getViewSettings = useEffectEvent(() => viewSettings)

  // useEffectEvent: read location center (fixed scene origin, distinct from camera center)
  const getLocationCenter = useEffectEvent(() => locationCenter)

  // useEffectEvent: wrap onReady so it doesn't need to be in effect deps
  const getOnReady = useEffectEvent(() => onReady)

  // Initialize map — deferred until viewSettings are ready (location data loaded)
  // Uses useEffectEvent to avoid stale closures without triggering re-init
  useEffect(() => {
    // Guard: Prevent re-initialization if map already exists
    if (mapRef.current) {
      return
    }

    // Wait for location view settings before creating map (prevents Detroit flash)
    if (!viewSettingsReady) {
      return
    }

    if (!mapContainerRef.current || !MAPBOX_TOKEN) {
      console.error('MapboxScene: Missing container or token')
      return
    }

    setLoading(true)

    // Set Mapbox token
    mapboxgl.accessToken = MAPBOX_TOKEN

    // Read latest view settings via useEffectEvent (no stale closure)
    const vs = getViewSettings()
    if (!vs) return // Should not happen (viewSettingsReady guards), but type-safe

    // Get map style URL
    const mapStyle = getMapStyleUrl(mapStyleId)

    // Initialize Mapbox map at the correct location center
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: vs.center,
      zoom: vs.zoom,
      pitch: vs.pitch,
      bearing: vs.bearing,
      antialias: true,
      keyboard: false,
      attributionControl: false,
    })

    map.addControl(new mapboxgl.AttributionControl({ compact: true }))

    mapRef.current = map

    // Handle map load
    map.on('load', () => {
      console.log('MapboxScene: Map loaded at', vs.center)
      activeStyleRef.current = useMapStore.getState().mapStyleId
      setIsMapLoaded(true)
      setLoading(false)

      // Store location center for aircraft interaction coordinate calculations
      // (fixed scene origin — NOT camera position which may differ from saved presets)
      const lc = getLocationCenter() ?? vs.center
      setMapCenter({ lat: lc[1], lng: lc[0] })
      // Record as "settled" — prevents the viewSettings effect from doing a redundant flyTo
      settledViewSettingsRef.current = JSON.stringify(vs)
    })

    // Handle style load to apply config (fires after initial load and style changes)
    map.on('style.load', () => {
      const currentStyle = useMapStore.getState().mapStyleId
      const currentConfig = useMapStore.getState().config.mapbox

      // Apply Mapbox config properties (Standard style only)
      applyMapboxConfig(map, currentConfig, currentStyle)

      // Setup 3D terrain
      setupTerrain(map, currentConfig.terrain.enabled, currentConfig.terrain.exaggeration)
    })

    // Debug: Log camera position on move.
    // Gated because `jumpTo` (used per-frame by the cinematic flythrough driver)
    // fires moveend synchronously — an ungated logger costs real frame budget.
    if (ENABLE_CAMERA_DEBUG) {
      map.on('moveend', () => {
        const center = map.getCenter()
        const zoom = map.getZoom()
        const pitch = map.getPitch()
        const bearing = map.getBearing()
        console.log(
          `Camera: center=[${center.lng.toFixed(6)}, ${center.lat.toFixed(6)}], ` +
          `zoom=${zoom.toFixed(2)}, pitch=${pitch.toFixed(1)}, bearing=${bearing.toFixed(1)}`
        )
      })
    }

    // Cleanup
    return () => {
      // Remove custom layer first (stops Mapbox render callbacks before teardown)
      if (map.getLayer('three-js-layer')) {
        map.removeLayer('three-js-layer')
      }
      try {
        layerServiceRef.current?.dispose()
      } catch (e) {
        console.warn('MapboxScene: dispose error during cleanup', e)
      }
      map.remove()
      mapRef.current = null
      layerServiceRef.current = null
      setIs3DLayerAdded(false)
    }
  }, [viewSettingsReady]) // Only re-check when readiness changes

  // Handle tab visibility changes to prevent map reload
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        map.resize()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isMapLoaded])

  // Resize map when container dimensions change (e.g. sidebar hide/show)
  useEffect(() => {
    const map = mapRef.current
    const container = mapContainerRef.current
    if (!map || !container || !isMapLoaded) return

    const ro = new ResizeObserver(() => {
      map.resize()
    })
    ro.observe(container)

    return () => ro.disconnect()
  }, [isMapLoaded])

  // Tear down and recreate Three.js layer when location changes
  // Uses jumpTo (instant) instead of flyTo for cross-location switches
  useEffect(() => {
    // First mount — just record the initial locationId
    if (activeLocationIdRef.current === undefined) {
      activeLocationIdRef.current = locationId
      return
    }
    // Same location — nothing to do
    if (locationId === activeLocationIdRef.current) return

    console.log(`MapboxScene: Location changed ${activeLocationIdRef.current} → ${locationId}`)
    activeLocationIdRef.current = locationId
    // Reset so the first positioning at the new location is instant
    hasInitialPositionRef.current = false

    // Reset location-scoped stores before tearing down 3D layer
    useAircraftEditorStore.getState().resetForLocationSwitch()
    useCollisionStore.getState().clearAllCollisions()
    setLoaded3DIds(new Set())

    const map = mapRef.current
    const svc = layerServiceRef.current

    if (map && svc && is3DLayerAdded) {
      // Clear collision/highlight visual state before disposing objects
      svc.clearCollisionState()
      svc.clearHighlightState()

      // Dispose Three.js objects
      svc.clearBuildings()
      svc.clearAircraft()

      // Remove custom layer from Mapbox
      if (map.getLayer('three-js-layer')) {
        map.removeLayer('three-js-layer')
      }
      layerServiceRef.current = null

      // Trigger layer recreation
      setIs3DLayerAdded(false)

      // Clear stale overlay data in parent
      const onReadyFn = getOnReady()
      onReadyFn?.({ map, sceneObjects: null, layerService: null }, [])
    }

    // Jump camera to new location (instant, no animation for cross-location)
    if (map && isMapLoaded) {
      const vs = getViewSettings()
      const lc = getLocationCenter()
      if (vs) {
        // Use location center as fixed scene origin for coordinate calculations
        const origin = lc ?? vs.center
        setMapCenter({ lat: origin[1], lng: origin[0] })
        map.jumpTo({
          center: vs.center,
          zoom: vs.zoom,
          pitch: vs.pitch,
          bearing: vs.bearing,
        })
        settledViewSettingsRef.current = JSON.stringify(vs)
      }
      // If vs is undefined, the viewSettings effect will handle positioning when data arrives
    }
  }, [locationId, is3DLayerAdded, isMapLoaded])

  // Create empty Three.js layer as soon as map is loaded (no longer waits for buildings)
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded || is3DLayerAdded) return

    // Read location center — must be available for correct transform matrix
    // Fallback to initialViewSettings.center for standalone/demo mode (no locationId)
    const lc = getLocationCenter() ?? (getViewSettings()?.center as [number, number] | undefined)
    if (!lc) return // Location data not ready yet — will retry when data arrives

    // Create layer service
    const layerService = new MapboxCustomLayerService()
    layerServiceRef.current = layerService

    // Create empty scene (buildings + aircraft stream in later)
    const customLayer = layerService.createThreeJsLayer({
      center: lc,
      altitude: -3,
      enableShadows,
      shadowQuality: 'high',
      buildings: [],
      aircraft: [],
      onSceneReady: () => {
        console.log('3D Scene ready (empty — buildings + aircraft will stream in)')
      },
    })

    map.addLayer(customLayer)
    setIs3DLayerAdded(true)
    activeLocationIdRef.current = locationId

    console.log('MapboxScene: Empty Three.js layer added, streaming content in...')
  }, [isMapLoaded, is3DLayerAdded])

  // Stream buildings into scene one-by-one via idle callbacks
  useEffect(() => {
    if (!layerServiceRef.current || !is3DLayerAdded || buildingsToRender.length === 0) return

    const scheduler = new IdleScheduler()
    buildingsToRender.forEach((config) => {
      scheduler.enqueue(() => {
        layerServiceRef.current?.addBuildingFromConfig(config, { enableShadows })
      })
    })
    // Signal obstacle hook to push after all buildings are in the scene
    scheduler.enqueue(() => {
      setBuildingSyncTrigger(v => v + 1)
    })

    return () => scheduler.clear()
  }, [is3DLayerAdded, buildingsToRender])

  // Handle map style changes — reattach existing scene instead of full rebuild
  // map.setStyle() removes custom layers from Mapbox's layer list, but the Three.js
  // scene/renderer/meshes live independently on layerServiceRef. We re-register a
  // lightweight layer wrapper that reuses the existing scene graph.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded) return

    // Skip redundant setStyle when the style hasn't actually changed
    // (e.g., on initial mount when isMapLoaded flips to true)
    if (activeStyleRef.current === mapStyleId) return
    activeStyleRef.current = mapStyleId

    const newStyle = getMapStyleUrl(mapStyleId)

    map.once('style.load', () => {
      const svc = layerServiceRef.current
      if (svc?.hasScene()) {
        try {
          // Reattach existing scene — no rebuild needed
          map.addLayer(svc.createReattachLayer())
          map.triggerRepaint()
        } catch (e) {
          console.warn('MapboxScene: reattach failed, triggering full rebuild', e)
          setIs3DLayerAdded(false)
        }
      } else {
        // No scene yet (first load or after location change) — full rebuild
        setIs3DLayerAdded(false)
      }
    })

    map.setStyle(newStyle)
  }, [mapStyleId, isMapLoaded])

  // Handle view settings changes (camera position)
  // First positioning is instant (jumpTo), subsequent changes animate (flyTo)
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded || !viewSettings) return

    // Skip if camera was already positioned here (by map init or location jumpTo)
    const vsKey = JSON.stringify(viewSettings)
    if (settledViewSettingsRef.current === vsKey) return
    settledViewSettingsRef.current = vsKey

    if (!hasInitialPositionRef.current) {
      // First positioning after load — instant, no animation
      hasInitialPositionRef.current = true
      map.jumpTo({
        center: viewSettings.center,
        zoom: viewSettings.zoom,
        pitch: viewSettings.pitch,
        bearing: viewSettings.bearing,
      })
    } else {
      // User switched view preset — animate
      map.flyTo({
        center: viewSettings.center,
        zoom: viewSettings.zoom,
        pitch: viewSettings.pitch,
        bearing: viewSettings.bearing,
        duration: 1500,
      })
    }
  }, [viewSettings, isMapLoaded])

  // Handle mapbox config changes at runtime
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded) return

    // Apply config properties
    applyMapboxConfig(map, mapboxConfig, mapStyleId)

    // Update terrain
    setupTerrain(map, mapboxConfig.terrain.enabled, mapboxConfig.terrain.exaggeration)
  }, [mapboxConfig, mapStyleId, isMapLoaded])

  // Create stable key for aircraft data to properly trigger re-renders
  // Includes renderMode so presence changes (away→present) trigger re-render
  const aircraftDataKey = useMemo(
    () => dbAircraft.map((a) => `${a.id}:${a.renderMode ?? 'solid'}`).sort().join(','),
    [dbAircraft]
  )

  // Aircraft render mode from store (triggers full reload when toggled)
  const aircraftRenderMode = useRampViewConfigStore((state) => state.aircraftRenderMode)
  const forceProceduralMode = aircraftRenderMode === 'simplified'

  // Track previous render mode to detect changes
  const prevRenderModeRef = useRef(aircraftRenderMode)

  // Load/update aircraft incrementally (diff-based, not clear-all/reload-all)
  // Adding 1 aircraft no longer reloads all 20 — only the delta is processed
  // When render mode changes, all aircraft are reloaded with the new mode
  useEffect(() => {
    const layerService = layerServiceRef.current
    if (!layerService || !is3DLayerAdded) return

    const sceneObjects = layerService.getSceneObjects()
    if (!sceneObjects.scene) return

    const finishAndNotify = () => {
      const onReadyFn = getOnReady()
      onReadyFn?.(
        {
          map: mapRef.current,
          sceneObjects,
          layerService,
        },
        dbAircraft
      )
    }

    // Owner-of-aircraft opt-out. This is load-bearing, not just an optimization:
    // the diff below removes every scene object whose name isn't in `dbAircraft`,
    // which would delete a caller's imperatively-added actors. Callers still need
    // `onReady` to capture the layer service, so notify before bailing.
    if (!renderAllocations) {
      finishAndNotify()
      return
    }

    const renderModeChanged = prevRenderModeRef.current !== aircraftRenderMode
    prevRenderModeRef.current = aircraftRenderMode

    // Objects a caller added imperatively (event-planner actors, procedural
    // tugs/trucks). They aren't in `dbAircraft` and never will be, so every
    // removal path below must skip them — otherwise this diff silently deletes
    // another subsystem's scene the moment the allocation data key changes.
    const isExternal = (obj: THREE.Object3D) => obj.userData.externalOwner === true

    // If render mode changed, remove ALL aircraft and re-add all
    if (renderModeChanged) {
      const allIds = sceneObjects.aircraft.filter((a) => !isExternal(a)).map((a) => a.name)
      allIds.forEach((id) => layerService.removeAircraft(id))
    }

    // Diff: what's in the scene vs what should be
    const currentIds = new Set(
      sceneObjects.aircraft.filter((a) => !isExternal(a)).map((a) => a.name)
    )
    const desiredIds = new Set(dbAircraft.map((a) => a.id))

    // Detect render mode changes on existing aircraft (e.g., ghost_away→solid after arrival)
    const desiredRenderModes = new Map(dbAircraft.map((a) => [a.id, a.renderMode ?? 'solid']))
    const renderModeStale: string[] = []
    for (const obj of sceneObjects.aircraft) {
      const desired = desiredRenderModes.get(obj.name)
      if (desired && obj.userData.renderMode !== desired) {
        renderModeStale.push(obj.name)
      }
    }
    renderModeStale.forEach((id) => layerService.removeAircraft(id))

    // REMOVE aircraft no longer in data
    const toRemove = [...currentIds].filter((id) => !desiredIds.has(id))
    toRemove.forEach((id) => layerService.removeAircraft(id))

    // ADD aircraft not yet in scene (including those removed for render mode change)
    const updatedCurrentIds = new Set(sceneObjects.aircraft.map((a) => a.name))
    const toAdd = dbAircraft.filter((a) => !updatedCurrentIds.has(a.id))

    if (toAdd.length > 0) {
      const loader = layerService.getAircraftLoader()
      if (!loader) { finishAndNotify(); return }

      loader.loadAircraftBatch(toAdd, forceProceduralMode, {
        onTypeGroupLoaded: (models, typeCode) => {
          // Stream each type group into the scene immediately
          models.forEach((model) => {
            sceneObjects.scene!.add(model)
            sceneObjects.aircraft.push(model)
          })
          layerService.markSceneDirty()
          console.log(`MapboxScene: +${models.length} ${typeCode} streamed in`)

          // Track loaded IDs so sprite overlay can fade them out
          setLoaded3DIds((prev) => {
            const next = new Set(prev)
            models.forEach((m) => next.add(m.name))
            return next
          })
        },
      }).then(() => {
        console.log(`MapboxScene: All aircraft loaded, -${toRemove.length} removed (mode: ${aircraftRenderMode})`)
        finishAndNotify()
      }).catch((error) => {
        console.error('Error loading aircraft:', error)
        finishAndNotify()
      })
    } else {
      if (toRemove.length > 0) {
        console.log(`MapboxScene: -${toRemove.length} removed`)
      }
      finishAndNotify()
    }
  }, [is3DLayerAdded, aircraftDataKey, aircraftRenderMode, renderAllocations]) // Re-run when render mode changes

  // Roof visibility toggle — hide/show all "roof"-named groups in buildings
  const showRoofs = useRampViewConfigStore((state) => state.showRoofs)
  useEffect(() => {
    const layerService = layerServiceRef.current
    if (!layerService || !is3DLayerAdded) return
    layerService.setRoofVisibility(showRoofs)
  }, [showRoofs, is3DLayerAdded])

  // Aircraft highlighting effect (hover and selection)
  // Uses in-place emissive mutation — no material cloning, no iterate-all-aircraft
  useEffect(() => {
    if (!interactionEnabled) return

    const layerService = layerServiceRef.current
    if (!layerService) return

    const sceneObjects = layerService.getSceneObjects()
    if (!sceneObjects.scene) return

    const emissiveCache = originalEmissivesRef.current

    // Helper: set emissive highlight on a single aircraft (no material clone)
    const setHighlight = (aircraft: THREE.Object3D, hex: number) => {
      aircraft.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
          if (!emissiveCache.has(child.uuid)) {
            emissiveCache.set(child.uuid, child.material.emissive.clone())
          }
          child.material.emissive.setHex(hex)
        }
      })
    }

    // Helper: restore original emissive on a single aircraft
    const clearHighlight = (aircraft: THREE.Object3D) => {
      aircraft.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
          const original = emissiveCache.get(child.uuid)
          if (original) {
            child.material.emissive.copy(original)
            emissiveCache.delete(child.uuid)
          }
        }
      })
    }

    // Clear previous hover highlight (only that one aircraft, not all)
    const prevHovered = prevHoveredRef.current
    if (prevHovered && prevHovered !== hoveredAllocationId && prevHovered !== selectedAllocationId) {
      const prevAircraft = sceneObjects.aircraft.find((a) => a.name === prevHovered)
      if (prevAircraft && !collidingAircraftIds.has(prevHovered)) {
        clearHighlight(prevAircraft)
      }
    }

    // Clear previous selection highlight
    const prevSelected = prevSelectedRef.current
    if (prevSelected && prevSelected !== selectedAllocationId && prevSelected !== hoveredAllocationId) {
      const prevAircraft = sceneObjects.aircraft.find((a) => a.name === prevSelected)
      if (prevAircraft && !collidingAircraftIds.has(prevSelected)) {
        clearHighlight(prevAircraft)
      }
    }

    // Apply hover highlight (subtle emissive glow) — skip colliding aircraft
    if (hoveredAllocationId && hoveredAllocationId !== selectedAllocationId) {
      const hoveredAircraft = sceneObjects.aircraft.find((a) => a.name === hoveredAllocationId)
      if (hoveredAircraft && !collidingAircraftIds.has(hoveredAllocationId)) {
        setHighlight(hoveredAircraft, 0x333333)
      }
    }

    // Apply selection highlight (stronger emissive glow) — skip colliding aircraft
    if (selectedAllocationId) {
      const selectedAircraft = sceneObjects.aircraft.find((a) => a.name === selectedAllocationId)
      if (selectedAircraft && !collidingAircraftIds.has(selectedAllocationId)) {
        setHighlight(selectedAircraft, 0x444444)
      }
    }

    // Update refs for next diff
    prevHoveredRef.current = hoveredAllocationId
    prevSelectedRef.current = selectedAllocationId

    // Trigger repaint
    layerService.markSceneDirty()
  }, [hoveredAllocationId, selectedAllocationId, interactionEnabled, collidingAircraftIds])

  return (
    <div className={`relative w-full h-full min-h-[400px] ${className}`}>
      {/* Map container */}
      <div
        ref={mapContainerRef}
        className="absolute inset-0 w-full h-full"
      />

      {/* Loading indicator — only blocks on view settings (map needs initial position) */}
      {!viewSettingsReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">Loading location...</span>
          </div>
        </div>
      )}

      {/* Custom overlay (for aircraft action buttons).
          Hidden in cinema mode — this is the single choke point that blanks the
          event planner's entire chrome, since all of it lives in renderOverlay. */}
      {isMapLoaded && renderOverlay && !cinemaActive && (
        <div className="absolute inset-0 pointer-events-none z-10">
          {renderOverlay(mapRef.current, dbAircraft, loaded3DIds)}
        </div>
      )}

      {/* Controls overlay — lives inside the scene, so page-level cinema
          wrappers can't reach it; it has to opt out here. */}
      {isMapLoaded && !cinemaActive && (
        <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
          {/* Map style selector */}
          <MapStyleSelector />
        </div>
      )}
    </div>
  )
}

